import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { fpColors } from '../../../styles/theme';

/**
 * BorrowRequestsEntryCard — FE-254.
 *
 * The Share tab's persistent entry point into BorrowRequestsManager.
 * Before this card, the only way to reach the borrow-requests inbox
 * was tapping a notification card. If a user dismissed the notification
 * they were stuck with no way back. This card lives on Share so it's
 * always one tap away.
 *
 * Behavior:
 *   - Two chips: pendingReceived (action needed on items you own) +
 *     pendingSent (waiting on someone else).
 *   - pendingReceived chip is tinted error/red when > 0 to nudge the
 *     user toward the action they need to take.
 *   - Tap routes to SharingRequests with `initialTab` set to whichever
 *     tab has live action (received first, then sent, falls back to
 *     sent when both are 0).
 *   - Empty-history state (no historical requests in either direction)
 *     renders no chips, just a subtle "Manage your borrow requests
 *     here when they start coming in" subtitle.
 *
 * The card READS from the store — it does not refetch. ShareFlowScreen
 * already dispatches loadReceivedRequests + loadSentRequests on mount,
 * so the counts are fresh by the time this renders.
 */

interface BorrowRequestsEntryCardProps {
  pendingReceived: number;
  pendingSent: number;
  /** Total historical requests across both directions. When 0, render
   *  the empty-state subtitle so first-time users get context. */
  totalHistorical: number;
}

export const BorrowRequestsEntryCard: React.FC<BorrowRequestsEntryCardProps> = ({
  pendingReceived,
  pendingSent,
  totalHistorical,
}) => {
  const navigation = useNavigation<any>();
  const isEmptyHistory = totalHistorical === 0;
  const hasAnyPending = pendingReceived > 0 || pendingSent > 0;

  // Open whichever tab has live action; falls back to sent so a fresh
  // account still lands somewhere meaningful.
  const handlePress = () => {
    const initialTab: 'received' | 'sent' =
      pendingReceived > 0 ? 'received' : 'sent';
    navigation.navigate('SharingRequests', { initialTab });
  };

  return (
    <Pressable
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel="Open borrow requests"
      testID="borrow-requests-entry-card"
      style={({ pressed }) => [
        styles.card,
        pressed && { opacity: 0.85 },
      ]}
    >
      <View style={styles.iconBubble}>
        <MaterialIcons
          name="inbox"
          size={22}
          color={fpColors.primary}
        />
      </View>

      <View style={styles.body}>
        <Text style={styles.title}>Borrow requests</Text>
        {isEmptyHistory ? (
          <Text style={styles.subtitle} numberOfLines={2}>
            Manage your borrow requests here when they start coming in.
          </Text>
        ) : hasAnyPending ? (
          <View style={styles.chipsRow}>
            {pendingReceived > 0 ? (
              <View style={[styles.chip, styles.chipReceived]}>
                <Text style={[styles.chipText, styles.chipReceivedText]}>
                  {`${pendingReceived} new`}
                </Text>
              </View>
            ) : null}
            {pendingSent > 0 ? (
              <View style={[styles.chip, styles.chipSent]}>
                <Text style={[styles.chipText, styles.chipSentText]}>
                  {`${pendingSent} sent`}
                </Text>
              </View>
            ) : null}
          </View>
        ) : (
          <Text style={styles.subtitle} numberOfLines={1}>
            No pending requests.
          </Text>
        )}
      </View>

      <MaterialIcons
        name="chevron-right"
        size={22}
        color={fpColors.outline}
      />
    </Pressable>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    borderRadius: 14,
    backgroundColor: fpColors.surface,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    marginBottom: 16,
  },
  iconBubble: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: fpColors.primaryContainer,
  },
  body: { flex: 1, minWidth: 0, gap: 4 },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 15,
    color: fpColors.onSurface,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12.5,
    color: fpColors.outline,
    lineHeight: 16,
  },
  chipsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  chip: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
  },
  chipReceived: {
    backgroundColor: `${fpColors.error}1a`,
  },
  chipReceivedText: {
    color: fpColors.error,
  },
  chipSent: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  chipSentText: {
    color: fpColors.outline,
  },
  chipText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 11,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
});

export default BorrowRequestsEntryCard;
